import React, { useState, useRef, useEffect } from "react";
import {
  FaVolumeUp,
  FaVolumeMute,
  FaRedo,
  FaPalette,
  FaCheckCircle,
  FaPencilAlt,
  FaStar,
  FaMicrophone,
  FaUser,
  FaSmile
} from "react-icons/fa";

/**
 * Cambridge-Style Lowercase Alphabet Cues & Guides (a-z)
 * Uses normalized SVG vector path strings for smooth, gapless geometry.
 */
const ALPHABET_DATA = {
  a: {
    label: "a",
    example: "Apple 🍎",
    rhyme: "Make a round circle, then a straight line down right next to it — that makes a lowercase a!",
    paths: [
      "M 0.58 0.42 C 0.58 0.32, 0.38 0.32, 0.38 0.42 C 0.38 0.52, 0.58 0.52, 0.58 0.42",
      "M 0.58 0.32 L 0.58 0.68 C 0.58 0.75, 0.50 0.78, 0.42 0.75"
    ]
  },
  b: {
    label: "b",
    example: "Ball ⚽",
    rhyme: "Start at the top, go straight down, and make a round belly at the bottom — that makes a b!",
    paths: [
      "M 0.38 0.2 L 0.38 0.8",
      "M 0.38 0.5 C 0.62 0.5, 0.62 0.8, 0.38 0.8"
    ]
  },
  c: {
    label: "c",
    example: "Cat 🐱",
    rhyme: "Curve around like a little crescent moon — that makes a c!",
    paths: [
      "M 0.62 0.35 C 0.38 0.35, 0.38 0.65, 0.62 0.65"
    ]
  },
  d: {
    label: "d",
    example: "Dog 🐶",
    rhyme: "Make a round circle first, then a tall stick straight down — that makes a d!",
    paths: [
      "M 0.42 0.35 C 0.62 0.35, 0.62 0.65, 0.42 0.65",
      "M 0.62 0.2 L 0.62 0.8"
    ]
  },
  e: {
    label: "e",
    example: "Elephant 🐘",
    rhyme: "Little line across, then loop around — that makes an e!",
    paths: [
      "M 0.38 0.52 L 0.62 0.52 C 0.62 0.38, 0.38 0.38, 0.38 0.52 C 0.38 0.68, 0.62 0.68, 0.62 0.58"
    ]
  },
  f: {
    label: "f",
    example: "Fish 🐟",
    rhyme: "Curve over the top, go straight down, and put a belt across — that makes an f!",
    paths: [
      "M 0.58 0.22 C 0.50 0.18, 0.40 0.22, 0.40 0.32 L 0.40 0.82",
      "M 0.32 0.45 L 0.55 0.45"
    ]
  },
  g: {
    label: "g",
    example: "Grape 🍇",
    rhyme: "Make a round circle, go down, and hook it back up like a tail — that makes a g!",
    paths: [
      "M 0.62 0.35 C 0.38 0.35, 0.38 0.65, 0.62 0.65",
      "M 0.62 0.35 L 0.62 0.78 C 0.62 0.88, 0.38 0.88, 0.38 0.75"
    ]
  },
  h: {
    label: "h",
    example: "Hat 🎩",
    rhyme: "Tall line down, bounce back up and over — that makes an h!",
    paths: [
      "M 0.38 0.2 L 0.38 0.8",
      "M 0.38 0.5 C 0.50 0.38, 0.62 0.38, 0.62 0.8"
    ]
  },
  i: {
    label: "i",
    example: "Ice Cream 🍦",
    rhyme: "Short line down, and a dot right on top — that makes an i!",
    paths: [
      "M 0.50 0.32 L 0.50 0.78",
      "M 0.50 0.22 L 0.50 0.24"
    ]
  },
  j: {
    label: "j",
    example: "Jug 🏺",
    rhyme: "Line down with a hook at the bottom, and a dot on top — that makes a j!",
    paths: [
      "M 0.55 0.32 L 0.55 0.75 C 0.55 0.85, 0.38 0.85, 0.38 0.75",
      "M 0.55 0.22 L 0.55 0.24"
    ]
  },
  k: {
    label: "k",
    example: "Kite 🪁",
    rhyme: "Tall line down, slide in and slide out — that makes a k!",
    paths: [
      "M 0.38 0.2 L 0.38 0.8",
      "M 0.62 0.38 L 0.38 0.58",
      "M 0.45 0.52 L 0.62 0.8"
    ]
  },
  l: {
    label: "l",
    example: "Lion 🦁",
    rhyme: "A simple straight line from top to bottom — that makes an l!",
    paths: [
      "M 0.50 0.2 L 0.50 0.8"
    ]
  },
  m: {
    label: "m",
    example: "Monkey 🐵",
    rhyme: "Down, hump, and another hump — that makes an m!",
    paths: [
      "M 0.32 0.35 L 0.32 0.78",
      "M 0.32 0.42 C 0.42 0.32, 0.50 0.32, 0.50 0.78",
      "M 0.50 0.42 C 0.60 0.32, 0.68 0.32, 0.68 0.78"
    ]
  },
  n: {
    label: "n",
    example: "Nest 🪹",
    rhyme: "Down, and bounce over into a hump — that makes an n!",
    paths: [
      "M 0.38 0.35 L 0.38 0.78",
      "M 0.38 0.45 C 0.50 0.32, 0.62 0.32, 0.62 0.78"
    ]
  },
  o: {
    label: "o",
    example: "Owl 🦉",
    rhyme: "Round and round like a little circle — that makes an o!",
    paths: [
      "M 0.50 0.32 C 0.32 0.32, 0.32 0.78, 0.50 0.78 C 0.68 0.78, 0.68 0.32, 0.50 0.32"
    ]
  },
  p: {
    label: "p",
    example: "Pen 🖊️",
    rhyme: "Line down deep, and a round circle at the top — that makes a p!",
    paths: [
      "M 0.38 0.32 L 0.38 0.88",
      "M 0.38 0.35 C 0.62 0.35, 0.62 0.65, 0.38 0.65"
    ]
  },
  q: {
    label: "q",
    example: "Queen 👑",
    rhyme: "Round circle, line down, and a little kick at the end — that makes a q!",
    paths: [
      "M 0.62 0.35 C 0.38 0.35, 0.38 0.65, 0.62 0.65",
      "M 0.62 0.35 L 0.62 0.88 C 0.62 0.92, 0.72 0.92, 0.75 0.88"
    ]
  },
  r: {
    label: "r",
    example: "Rabbit 🐰",
    rhyme: "Line down, and a little branch popping out — that makes an r!",
    paths: [
      "M 0.38 0.35 L 0.38 0.78",
      "M 0.38 0.52 C 0.50 0.38, 0.62 0.38, 0.62 0.45"
    ]
  },
  s: {
    label: "s",
    example: "Sun ☀️",
    rhyme: "Slide to the left, curve around, and slide back down — that makes an s!",
    paths: [
      "M 0.62 0.38 C 0.50 0.32, 0.38 0.38, 0.38 0.50 C 0.38 0.60, 0.62 0.62, 0.62 0.72 C 0.62 0.82, 0.38 0.80, 0.38 0.75"
    ]
  },
  t: {
    label: "t",
    example: "Tree 🌳",
    rhyme: "Tall stick down with a cross in the middle — that makes a t!",
    paths: [
      "M 0.50 0.22 L 0.50 0.78",
      "M 0.38 0.42 L 0.62 0.42"
    ]
  },
  u: {
    label: "u",
    example: "Umbrella ☂️",
    rhyme: "Down, curve across the bottom, and straight up — that makes a u!",
    paths: [
      "M 0.38 0.35 L 0.38 0.68 C 0.38 0.78, 0.62 0.78, 0.62 0.68 L 0.62 0.35"
    ]
  },
  v: {
    label: "v",
    example: "Van 🚐",
    rhyme: "Slide down and slide right back up — that makes a v!",
    paths: [
      "M 0.35 0.35 L 0.50 0.78 L 0.65 0.35"
    ]
  },
  w: {
    label: "w",
    example: "Watch ⌚",
    rhyme: "Down, up, down, up — like little hills — that makes a w!",
    paths: [
      "M 0.32 0.35 L 0.40 0.78 L 0.50 0.52 L 0.60 0.78 L 0.68 0.35"
    ]
  },
  x: {
    label: "x",
    example: "Xylophone 🪘",
    rhyme: "Cross across from corner to corner — that makes an x!",
    paths: [
      "M 0.38 0.35 L 0.62 0.78",
      "M 0.62 0.35 L 0.38 0.78"
    ]
  },
  y: {
    label: "y",
    example: "Yak 🐃",
    rhyme: "Diagonal down, then a long line down with a hook — that makes a y!",
    paths: [
      "M 0.38 0.35 L 0.50 0.58",
      "M 0.62 0.35 L 0.38 0.85 C 0.38 0.92, 0.28 0.92, 0.24 0.85"
    ]
  },
  z: {
    label: "z",
    example: "Zebra 🦓",
    rhyme: "Across, slide down diagonally, and across again — that makes a z!",
    paths: [
      "M 0.38 0.35 L 0.62 0.35 L 0.38 0.78 L 0.62 0.78"
    ]
  }
};

const drawNormalizedPath = (ctx, pathStr, width, height) => {
  const tokens = pathStr.trim().split(/\s+/);
  ctx.beginPath();
  let i = 0;

  while (i < tokens.length) {
    const cmd = tokens[i];
    if (cmd === "M") {
      ctx.moveTo(parseFloat(tokens[i + 1]) * width, parseFloat(tokens[i + 2]) * height);
      i += 3;
    } else if (cmd === "L") {
      ctx.lineTo(parseFloat(tokens[i + 1]) * width, parseFloat(tokens[i + 2]) * height);
      i += 3;
    } else if (cmd === "C") {
      ctx.bezierCurveTo(
        parseFloat(tokens[i + 1]) * width,
        parseFloat(tokens[i + 2]) * height,
        parseFloat(tokens[i + 3]) * width,
        parseFloat(tokens[i + 4]) * height,
        parseFloat(tokens[i + 5]) * width,
        parseFloat(tokens[i + 6]) * height
      );
      i += 7;
    } else {
      i++;
    }
  }
  ctx.stroke();
};

export default function LowercaseAlphabetTracingGame() {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const [selectedLetter, setSelectedLetter] = useState("a");
  const [isDrawing, setIsDrawing] = useState(false);
  const [selectedColor, setSelectedColor] = useState("#2563EB");
  const [brushSize, setBrushSize] = useState(14);
  const [aiSpeech, setAiSpeech] = useState("");
  const [tracingResult, setTracingResult] = useState(null);
  const [isMuted, setIsMuted] = useState(false);

  const [userName, setUserName] = useState("Little Star");
  const [inputName, setInputName] = useState("");
  const [showModal, setShowModal] = useState(true);
  const [isListening, setIsListening] = useState(false);

  const colors = [
    { name: "Royal Blue", hex: "#2563EB" },
    { name: "Ruby Red", hex: "#DC2626" },
    { name: "Emerald Green", hex: "#16A34A" },
    { name: "Sunny Yellow", hex: "#D97706" },
    { name: "Berry Purple", hex: "#9333EA" }
  ];

  const alphabetList = Object.keys(ALPHABET_DATA);

  const listenForName = () => {
    const SpeechRecognition =
      window.SpeechRecognition || window.webkitSpeechRecognition;

    if (!SpeechRecognition) {
      alert("Voice recognition isn't supported on this browser, but you can type your name!");
      return;
    }

    const recognition = new SpeechRecognition();
    recognition.lang = "en-US";
    recognition.interimResults = false;

    recognition.onstart = () => setIsListening(true);
    recognition.onend = () => setIsListening(false);

    recognition.onresult = (event) => {
      const spokenName = event.results[0][0].transcript.replace(/\.$/, "");
      if (spokenName) {
        setInputName(spokenName);
      }
    };

    recognition.start();
  };

  const handleSaveName = (nameToSave) => {
    const finalName = nameToSave && nameToSave.trim() !== "" ? nameToSave.trim() : "Little Star";
    setUserName(finalName);
    setShowModal(false);
    triggerInitialGreeting(finalName);
  };

  const triggerInitialGreeting = (name) => {
    const currentInfo = ALPHABET_DATA[selectedLetter];
    const text = `Hello ${name}! Let's trace the lowercase letter ${selectedLetter} for ${currentInfo.example}! ${currentInfo.rhyme}`;
    setAiSpeech(text);
    if (!isMuted) speakAi(text);
  };

  const drawGuideCanvas = () => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;

    // Use actual client bounding width/height so CSS and internal coordinates sync precisely 1:1
    const rect = container.getBoundingClientRect();
    const displayWidth = rect.width > 0 ? rect.width : (container.clientWidth || 600);
    const displayHeight = 400; // Fixed canvas working layout height

    canvas.width = displayWidth;
    canvas.height = displayHeight;

    const ctx = canvas.getContext("2d");

    ctx.fillStyle = "#FFFFFF";
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    ctx.lineWidth = 10;
    ctx.strokeStyle = "#CBD5E1";
    ctx.setLineDash([12, 12]);
    ctx.lineCap = "round";
    ctx.lineJoin = "round";

    const paths = ALPHABET_DATA[selectedLetter].paths;
    const w = canvas.width;
    const h = canvas.height;

    paths.forEach((pathStr) => {
      drawNormalizedPath(ctx, pathStr, w, h);
    });

    ctx.setLineDash([]);
  };

  useEffect(() => {
    const handleResize = () => {
      drawGuideCanvas();
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [selectedLetter]);

  useEffect(() => {
    // Slight timeout ensures DOM element is rendered and container width is computed accurately
    const timer = setTimeout(() => {
      drawGuideCanvas();
    }, 50);

    if (!showModal) {
      const currentInfo = ALPHABET_DATA[selectedLetter];
      const text = `Let's trace the lowercase letter ${selectedLetter}, ${userName}! Like ${currentInfo.example}. ${currentInfo.rhyme}`;
      setAiSpeech(text);
      if (!isMuted) {
        speakAi(text);
      }
    }
    setTracingResult(null);

    return () => clearTimeout(timer);
  }, [selectedLetter, showModal]);

  const speakAi = (text) => {
    if ("speechSynthesis" in window) {
      window.speechSynthesis.cancel();
      if (isMuted) return;
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.pitch = 1.2;
      utterance.rate = 0.85;
      window.speechSynthesis.speak(utterance);
    }
  };

  const toggleMute = () => {
    const nextMuteState = !isMuted;
    setIsMuted(nextMuteState);
    if (nextMuteState) {
      if ("speechSynthesis" in window) window.speechSynthesis.cancel();
    } else {
      speakAi(aiSpeech);
    }
  };

  const startDrawing = (e) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const rect = canvas.getBoundingClientRect();
    
    // Scale client coordinates proportionally to internal canvas width/height resolution
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;

    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;

    const x = (clientX - rect.left) * scaleX;
    const y = (clientY - rect.top) * scaleY;

    ctx.beginPath();
    ctx.moveTo(x, y);
    ctx.strokeStyle = selectedColor;
    ctx.lineWidth = brushSize;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";

    setIsDrawing(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const rect = canvas.getBoundingClientRect();
    
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;

    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;

    const x = (clientX - rect.left) * scaleX;
    const y = (clientY - rect.top) * scaleY;

    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const stopDrawing = () => {
    if (isDrawing) {
      setIsDrawing(false);
    }
  };

  const resetCanvas = () => {
    drawGuideCanvas();
    setTracingResult(null);
    const currentInfo = ALPHABET_DATA[selectedLetter];
    const text = `Let's try lowercase letter ${selectedLetter} again, ${userName}! ${currentInfo.rhyme}`;
    setAiSpeech(text);
    if (!isMuted) speakAi(text);
  };

  const calculateAccuracy = () => {
    const canvas = canvasRef.current;
    if (!canvas) return { percent: 0, reason: "empty" };

    const w = canvas.width;
    const h = canvas.height;
    const userCtx = canvas.getContext("2d");
    const userData = userCtx.getImageData(0, 0, w, h).data;

    const guideCanvas = document.createElement("canvas");
    guideCanvas.width = w;
    guideCanvas.height = h;
    const guideCtx = guideCanvas.getContext("2d");

    guideCtx.fillStyle = "#FFFFFF";
    guideCtx.fillRect(0, 0, w, h);
    guideCtx.lineWidth = 36;
    guideCtx.strokeStyle = "#000000";
    guideCtx.lineCap = "round";
    guideCtx.lineJoin = "round";

    const paths = ALPHABET_DATA[selectedLetter].paths;
    paths.forEach((pathStr) => {
      drawNormalizedPath(guideCtx, pathStr, w, h);
    });

    const guideData = guideCtx.getImageData(0, 0, w, h).data;

    let targetPixels = 0;
    let coveredTargetPixels = 0;
    let totalDrawnPixels = 0;
    let strayPixels = 0;

    for (let i = 0; i < userData.length; i += 16) {
      const r = userData[i];
      const g = userData[i + 1];
      const b = userData[i + 2];

      const isUserDrawn =
        !(r > 240 && g > 240 && b > 240) &&
        !(r > 190 && r < 215 && g > 200 && b > 220);

      const isTargetArea = guideData[i] < 50;

      if (isTargetArea) {
        targetPixels++;
        if (isUserDrawn) coveredTargetPixels++;
      }

      if (isUserDrawn) {
        totalDrawnPixels++;
        if (!isTargetArea) strayPixels++;
      }
    }

    if (totalDrawnPixels < 30) {
      return { percent: 0, reason: "empty" };
    }

    const coverageRatio = coveredTargetPixels / (targetPixels || 1);
    const precisionRatio = 1 - strayPixels / totalDrawnPixels;

    let finalPercent = Math.round((coverageRatio * 0.7 + precisionRatio * 0.3) * 100);
    finalPercent = Math.min(100, Math.max(0, finalPercent));

    return { percent: finalPercent, reason: "ok" };
  };

  const markComplete = () => {
    const { percent, reason } = calculateAccuracy();

    if (reason === "empty") {
      const text = `Try tracing lowercase letter ${selectedLetter} before checking, ${userName}!`;
      setAiSpeech(text);
      if (!isMuted) speakAi(text);
      setTracingResult(null);
      return;
    }

    let praise = "";
    let tier = "";

    if (percent >= 90) {
      tier = "Incredible!";
      praise = `Incredible ${userName}! ${percent}% accuracy! You are a tracing superstar!`;
    } else if (percent >= 75) {
      tier = "Excellent!";
      praise = `Excellent job, ${userName}! ${percent}% accuracy! Beautiful tracing!`;
    } else if (percent >= 55) {
      tier = "Good Job!";
      praise = `Good Job ${userName}! ${percent}% accuracy! Keep practicing lowercase letter ${selectedLetter}!`;
    } else if (percent >= 40) {
      tier = "Nice Try!";
      praise = `Nice try ${userName}! ${percent}% accuracy. Let's try staying closer to the lines!`;
    } else {
      tier = "Keep Practicing";
      praise = `Let's try again, ${userName}! Trace right over the dotted lines for ${selectedLetter}.`;
    }

    setTracingResult({ percent, label: tier });
    setAiSpeech(praise);
    if (!isMuted) speakAi(praise);
  };

  return (
    <div style={styles.container}>
      {showModal && (
        <div style={styles.modalOverlay}>
          <div style={styles.modalCard}>
            <div style={styles.modalHeader}>
              <FaSmile size={42} color="#2563EB" />
              <h2 style={styles.modalTitle}>Welcome Little Learner!</h2>
              <p style={styles.modalSubtitle}>What is your name today?</p>
            </div>

            <div style={styles.modalBody}>
              <button
                type="button"
                onClick={listenForName}
                style={{
                  ...styles.micPromptBtn,
                  backgroundColor: isListening ? "#EF4444" : "#2563EB"
                }}
              >
                <FaMicrophone size={22} color="#FFFFFF" />
                <span>{isListening ? "Listening... Speak now!" : "Click & Say Your Name"}</span>
              </button>

              <div style={styles.dividerRow}>
                <span style={styles.dividerText}>or type below</span>
              </div>

              <input
                type="text"
                placeholder="Enter your name..."
                value={inputName}
                onChange={(e) => setInputName(e.target.value)}
                style={styles.modalInput}
              />

              <button
                type="button"
                onClick={() => handleSaveName(inputName)}
                style={styles.modalStartBtn}
              >
                Let's Start Tracing! 🚀
              </button>
            </div>
          </div>
        </div>
      )}

      <div style={styles.card}>
        <div style={styles.header}>
          <div style={styles.headerLeft}>
            <div style={styles.iconCircle}>
              <FaPencilAlt size={20} color="#2563eb" />
            </div>
            <div>
              <h1 style={styles.title}>Lowercase Alphabet Tracing</h1>
              <p style={styles.subtitle}>Learn & Draw Letters a to z with Audio & Examples</p>
            </div>
          </div>

          <div style={styles.headerRight}>
            <div style={styles.nameInputBox}>
              <FaUser size={14} color="#2563EB" />
              <input
                type="text"
                placeholder="Enter Name"
                value={inputName}
                onChange={(e) => setInputName(e.target.value)}
                style={styles.headerInput}
              />
              <button
                onClick={() => handleSaveName(inputName)}
                style={styles.setNameBtn}
              >
                Set
              </button>
            </div>
          </div>
        </div>

        <div style={styles.numberSelectorBar}>
          <span style={styles.selectorLabel}>Choose a Letter:</span>
          <div style={styles.numberBtnGroup}>
            {alphabetList.map((letter) => (
              <button
                key={letter}
                onClick={() => setSelectedLetter(letter)}
                style={{
                  ...styles.numberChip,
                  backgroundColor: selectedLetter === letter ? "#2563EB" : "#F1F5F9",
                  color: selectedLetter === letter ? "#FFFFFF" : "#334155",
                  borderColor: selectedLetter === letter ? "#1D4ED8" : "#E2E8F0"
                }}
              >
                {letter}
              </button>
            ))}
          </div>
        </div>

        <div style={styles.speechBanner}>
          <button
            onClick={toggleMute}
            style={styles.muteBtn}
            title={isMuted ? "Unmute Voice" : "Mute Voice"}
          >
            {isMuted ? (
              <FaVolumeMute size={20} color="#DC2626" />
            ) : (
              <FaVolumeUp size={20} color="#1D4ED8" />
            )}
          </button>
          <p style={{ ...styles.speechText, opacity: isMuted ? 0.6 : 1 }}>
            "{aiSpeech}"
          </p>
        </div>

        {/* Dynamic Canvas Container with ~30% right example panel and fully mapped touch/mouse coordinates */}
        <div style={styles.canvasContainer} ref={containerRef}>
          <div style={styles.leftIndicatorPanel}>
            <span style={styles.largeLetterDisplay}>{selectedLetter}</span>
            <span style={styles.indicatorSubtext}>Lowercase</span>
          </div>

          <div style={styles.canvasWrapper}>
            <canvas
              ref={canvasRef}
              onMouseDown={startDrawing}
              onMouseMove={draw}
              onMouseUp={stopDrawing}
              onMouseLeave={stopDrawing}
              onTouchStart={startDrawing}
              onTouchMove={draw}
              onTouchEnd={stopDrawing}
              style={styles.canvas}
            />
          </div>

          <div style={styles.rightExamplePanel}>
            <span style={styles.exampleTitle}>Example</span>
            <span style={styles.exampleEmojiText}>{ALPHABET_DATA[selectedLetter].example}</span>
          </div>

          {tracingResult && (
            <div style={styles.completionBadge}>
              <FaStar size={16} color="#EAB308" /> {tracingResult.label} ({tracingResult.percent}%)
            </div>
          )}
        </div>

        <div style={styles.toolbar}>
          <div style={styles.colorPalette}>
            <FaPalette size={18} color="#64748B" style={{ marginRight: 6 }} />
            {colors.map((color) => (
              <button
                key={color.hex}
                onClick={() => setSelectedColor(color.hex)}
                style={{
                  ...styles.colorBtn,
                  backgroundColor: color.hex,
                  transform: selectedColor === color.hex ? "scale(1.15)" : "scale(1)",
                  border: selectedColor === color.hex ? "3px solid #0F172A" : "3px solid #FFFFFF"
                }}
                title={color.name}
              />
            ))}
          </div>

          <div style={styles.actionGroup}>
            <div style={styles.sizeSelector}>
              <span style={{ fontSize: "0.8rem", color: "#64748B", fontWeight: "bold" }}>Pencil:</span>
              <button
                onClick={() => setBrushSize(10)}
                style={{ ...styles.sizeBtn, fontWeight: brushSize === 10 ? "bold" : "normal" }}
              >
                Fine
              </button>
              <button
                onClick={() => setBrushSize(18)}
                style={{ ...styles.sizeBtn, fontWeight: brushSize === 18 ? "bold" : "normal" }}
              >
                Thick
              </button>
            </div>

            <button onClick={markComplete} style={styles.doneBtn}>
              <FaCheckCircle size={16} /> Check Tracing
            </button>

            <button onClick={resetCanvas} style={styles.resetBtn}>
              <FaRedo size={14} /> Clear Tracing
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

const styles = {
  container: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: "100vh",
    backgroundColor: "#EFF6FF",
    padding: "20px",
    boxSizing: "border-box",
    fontFamily: "Segoe UI, Tahoma, Geneva, Verdana, sans-serif"
  },
  modalOverlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(15, 23, 42, 0.65)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 1000,
    backdropFilter: "blur(4px)"
  },
  modalCard: {
    backgroundColor: "#FFFFFF",
    borderRadius: "24px",
    padding: "32px",
    width: "90%",
    maxWidth: "420px",
    boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.2)",
    textAlign: "center",
    border: "4px solid #93C5FD"
  },
  modalHeader: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "8px",
    marginBottom: "20px"
  },
  modalTitle: {
    margin: 0,
    color: "#1E293B",
    fontSize: "1.5rem",
    fontWeight: "800"
  },
  modalSubtitle: {
    margin: 0,
    color: "#64748B",
    fontSize: "0.95rem"
  },
  modalBody: {
    display: "flex",
    flexDirection: "column",
    gap: "14px"
  },
  micPromptBtn: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "10px",
    color: "#FFFFFF",
    border: "none",
    padding: "14px 20px",
    borderRadius: "14px",
    fontSize: "1rem",
    fontWeight: "bold",
    cursor: "pointer",
    transition: "background-color 0.2s ease"
  },
  dividerRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center"
  },
  dividerText: {
    fontSize: "0.8rem",
    color: "#94A3B8",
    fontWeight: "bold",
    textTransform: "uppercase"
  },
  modalInput: {
    padding: "12px 16px",
    borderRadius: "12px",
    border: "2px solid #CBD5E1",
    fontSize: "1rem",
    outline: "none",
    textAlign: "center"
  },
  modalStartBtn: {
    backgroundColor: "#16A34A",
    color: "#FFFFFF",
    border: "none",
    padding: "14px",
    borderRadius: "14px",
    fontSize: "1.05rem",
    fontWeight: "bold",
    cursor: "pointer"
  },
  card: {
    width: "100%",
    maxWidth: "960px",
    backgroundColor: "#FFFFFF",
    borderRadius: "24px",
    boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.1)",
    border: "4px solid #93C5FD",
    overflow: "hidden"
  },
  header: {
    background: "linear-gradient(135deg, #2563EB 0%, #1D4ED8 100%)",
    padding: "16px 24px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    color: "#FFFFFF",
    flexWrap: "wrap",
    gap: "12px"
  },
  headerLeft: {
    display: "flex",
    alignItems: "center",
    gap: "12px"
  },
  headerRight: {
    display: "flex",
    alignItems: "center"
  },
  nameInputBox: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    backgroundColor: "#FFFFFF",
    padding: "6px 12px",
    borderRadius: "12px"
  },
  headerInput: {
    border: "none",
    outline: "none",
    fontSize: "0.85rem",
    width: "100px",
    color: "#1E293B",
    fontWeight: "600"
  },
  setNameBtn: {
    backgroundColor: "#2563EB",
    color: "#FFFFFF",
    border: "none",
    padding: "4px 10px",
    borderRadius: "8px",
    fontSize: "0.75rem",
    fontWeight: "bold",
    cursor: "pointer"
  },
  iconCircle: {
    backgroundColor: "#FFFFFF",
    borderRadius: "50%",
    padding: "10px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center"
  },
  title: {
    margin: 0,
    fontSize: "1.3rem",
    fontWeight: 800
  },
  subtitle: {
    margin: 0,
    fontSize: "0.85rem",
    opacity: 0.95
  },
  numberSelectorBar: {
    backgroundColor: "#F8FAFC",
    padding: "10px 16px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    borderBottom: "1px solid #E2E8F0",
    flexWrap: "wrap",
    gap: "8px"
  },
  selectorLabel: {
    fontWeight: "bold",
    color: "#475569",
    fontSize: "0.85rem"
  },
  numberBtnGroup: {
    display: "flex",
    gap: "4px",
    flexWrap: "wrap",
    maxHeight: "80px",
    overflowY: "auto"
  },
  numberChip: {
    width: "32px",
    height: "32px",
    borderRadius: "8px",
    fontSize: "1rem",
    fontWeight: "bold",
    cursor: "pointer",
    border: "2px solid",
    transition: "all 0.15s ease",
    display: "flex",
    alignItems: "center",
    justifyContent: "center"
  },
  speechBanner: {
    backgroundColor: "#DBEAFE",
    padding: "10px 20px",
    display: "flex",
    alignItems: "center",
    gap: "12px",
    borderBottom: "2px solid #BFDBFE"
  },
  muteBtn: {
    background: "none",
    border: "none",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "4px",
    borderRadius: "6px"
  },
  speechText: {
    margin: 0,
    color: "#1E40AF",
    fontWeight: "600",
    fontStyle: "italic",
    fontSize: "0.95rem"
  },
  canvasContainer: {
    position: "relative",
    width: "100%",
    height: "400px",
    backgroundColor: "#FFFFFF",
    touchAction: "none",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between"
  },
  leftIndicatorPanel: {
    width: "15%",
    height: "100%",
    backgroundColor: "#F8FAFC",
    borderRight: "2px dashed #E2E8F0",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: "10px",
    boxSizing: "border-box"
  },
  largeLetterDisplay: {
    fontSize: "4.5rem",
    fontWeight: "900",
    color: "#2563EB",
    lineHeight: 1
  },
  indicatorSubtext: {
    fontSize: "0.75rem",
    color: "#64748B",
    fontWeight: "bold",
    marginTop: "8px",
    textTransform: "uppercase"
  },
  canvasWrapper: {
    width: "55%",
    height: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center"
  },
  rightExamplePanel: {
    width: "30%",
    height: "100%",
    backgroundColor: "#F8FAFC",
    borderLeft: "2px dashed #E2E8F0",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: "16px",
    boxSizing: "border-box",
    textAlign: "center"
  },
  exampleTitle: {
    fontSize: "0.85rem",
    color: "#64748B",
    fontWeight: "bold",
    textTransform: "uppercase",
    marginBottom: "12px"
  },
  exampleEmojiText: {
    fontSize: "1.8rem",
    fontWeight: "800",
    color: "#1E293B",
    wordBreak: "break-word"
  },
  canvas: {
    width: "100%",
    height: "100%",
    cursor: "crosshair",
    display: "block"
  },
  completionBadge: {
    position: "absolute",
    top: "14px",
    right: "32%",
    backgroundColor: "#FEF08A",
    color: "#854D0E",
    padding: "6px 14px",
    borderRadius: "20px",
    fontWeight: "bold",
    fontSize: "0.85rem",
    border: "1px solid #FDE047",
    display: "flex",
    alignItems: "center",
    gap: "6px",
    zIndex: 10
  },
  toolbar: {
    backgroundColor: "#F1F5F9",
    padding: "16px 20px",
    borderTop: "2px solid #E2E8F0",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: "12px"
  },
  colorPalette: {
    display: "flex",
    alignItems: "center",
    gap: "8px"
  },
  colorBtn: {
    width: "36px",
    height: "36px",
    borderRadius: "50%",
    cursor: "pointer",
    transition: "transform 0.15s ease",
    boxShadow: "0 2px 5px rgba(0,0,0,0.15)"
  },
  actionGroup: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    flexWrap: "wrap"
  },
  sizeSelector: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    backgroundColor: "#FFFFFF",
    padding: "4px 8px",
    borderRadius: "10px",
    border: "1px solid #CBD5E1"
  },
  sizeBtn: {
    background: "none",
    border: "none",
    color: "#334155",
    cursor: "pointer",
    fontSize: "0.8rem"
  },
  doneBtn: {
    drawGuideCanvas: "none",
    display: "flex",
    alignItems: "center",
    gap: "6px",
    backgroundColor: "#16A34A",
    color: "#FFFFFF",
    border: "none",
    padding: "10px 16px",
    borderRadius: "12px",
    fontWeight: "bold",
    fontSize: "0.9rem",
    cursor: "pointer"
  },
  resetBtn: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    backgroundColor: "#EF4444",
    color: "#FFFFFF",
    border: "none",
    padding: "10px 16px",
    borderRadius: "12px",
    fontWeight: "bold",
    fontSize: "0.9rem",
    cursor: "pointer"
  }
};